import { Button, InlineNotification, Layer, Stack, TextInput } from '@carbon/react';
import { BrandLogo, LanguageSwitcher, PoweredBy, StaffLogin } from '@pos/design-system';
import { currentLanguage, setLanguage, useTranslation } from '@pos/i18n';
import { useState, type FormEvent } from 'react';
import './LoginPage.scss';

/** The company's identity provider (ADR 0121): its button, and the break-glass password form behind a link. */
export interface LoginSso {
  /** The provider's name on the button, e.g. "Microsoft". */
  label: string;
  onStart(): void;
}

/** The second step after the password (ADR 0121): the authenticator app's code, or a recovery code. */
export interface LoginSecondFactor {
  /** Resolves with a translated error message, or undefined on success. */
  onSubmit(code: string): Promise<string | undefined>;
  onCancel(): void;
}

/**
 * Full-page login for the admin portals, with the language choice; the portal decides which endpoint it calls. At HO it can also
 * show the company's provider (then staff code and password are for break-glass accounts only), the second step for a code, and
 * a notice such as why a provider sign-in failed.
 */
export function LoginPage({
  onSubmit,
  sso,
  secondFactor,
  notice,
}: {
  onSubmit(staffCode: string, password: string): Promise<string | undefined>;
  sso?: LoginSso;
  secondFactor?: LoginSecondFactor;
  notice?: string;
}) {
  const { t } = useTranslation(); // re-render when the language changes
  const [breakGlass, setBreakGlass] = useState(false);
  return (
    <div className="pos-admin-login">
      <div className="pos-admin-login__panel">
        <BrandLogo />
        <LanguageSwitcher value={currentLanguage()} onChange={setLanguage} />
        {notice && <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={notice} />}
        {secondFactor ? (
          <CodeForm {...secondFactor} />
        ) : sso && !breakGlass ? (
          <Stack gap={5} className="pos-admin-login__sso">
            <h1 className="pos-admin-login__title">{t('login.title')}</h1>
            <Button size="lg" onClick={sso.onStart}>
              {t('login.sso', { name: sso.label })}
            </Button>
            <Button kind="ghost" size="sm" onClick={() => setBreakGlass(true)}>
              {t('login.breakGlass')}
            </Button>
          </Stack>
        ) : (
          <StaffLogin onSubmit={onSubmit} />
        )}
        <PoweredBy className="pos-admin-login__powered" />
      </div>
    </div>
  );
}

function CodeForm({ onSubmit, onCancel }: LoginSecondFactor) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    const message = await onSubmit(code.trim());
    setBusy(false);
    if (message) {
      setError(message);
      setCode('');
    }
  };

  return (
    <form className="pos-admin-login__code" onSubmit={(e) => void submit(e)} noValidate>
      <Stack gap={5}>
        <h1 className="pos-admin-login__title">{t('login.mfa.title')}</h1>
        <Layer>
          <TextInput
            id="pos-mfa-code"
            size="lg"
            labelText={t('login.mfa.code')}
            helperText={t('login.mfa.hint')}
            value={code}
            autoComplete="one-time-code"
            autoFocus
            onChange={(e) => setCode(e.target.value)}
          />
        </Layer>
        {error && <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={error} />}
        <Button type="submit" size="lg" disabled={busy || !code.trim()}>
          {t('login.submit')}
        </Button>
        <Button kind="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </Stack>
    </form>
  );
}
